import logger from '../../utils/logger';

interface QueueItem {
  id: string;
  operation: 'markAsRead' | 'delete' | 'update';
  timestamp: number;
  retryCount: number;
  data?: any;
}

/**
 * Service de file d'attente pour les opérations sur les notifications
 * Permet de garantir que les opérations sont effectuées même en cas d'erreur temporaire
 */
export const notificationQueueService = {
  /**
   * Nom de la clé dans le localStorage pour la file d'attente
   */
  QUEUE_KEY: 'notification_operations_queue',

  /**
   * Nombre maximum de tentatives pour une opération
   */
  MAX_RETRIES: 5,

  /**
   * Intervalle entre les tentatives (en millisecondes)
   * Utilise un backoff exponentiel
   */
  getRetryInterval(retryCount: number): number {
    return Math.min(1000 * Math.pow(2, retryCount), 30000); // Max 30 secondes
  },

  /**
   * Récupère la file d'attente depuis le localStorage
   */
  getQueue(): QueueItem[] {
    try {
      const queueJson = localStorage.getItem(this.QUEUE_KEY);
      if (!queueJson) return [];
      return JSON.parse(queueJson);
    } catch (error) {
      logger.error('Erreur lors de la récupération de la file d\'attente:', error);
      return [];
    }
  },

  /**
   * Sauvegarde la file d'attente dans le localStorage
   */
  saveQueue(queue: QueueItem[]): void {
    try {
      localStorage.setItem(this.QUEUE_KEY, JSON.stringify(queue));
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde de la file d\'attente:', error);
    }
  },

  /**
   * Ajoute une opération à la file d'attente
   */
  enqueue(id: string, operation: 'markAsRead' | 'delete' | 'update', data?: any): void {
    try {
      const queue = this.getQueue();
      
      // Vérifier si l'opération existe déjà pour éviter les doublons
      const existingIndex = queue.findIndex(item => item.id === id && item.operation === operation);
      
      if (existingIndex >= 0) {
        // Mettre à jour l'élément existant
        queue[existingIndex] = {
          ...queue[existingIndex],
          timestamp: Date.now(),
          data: data || queue[existingIndex].data
        };
      } else {
        // Ajouter un nouvel élément
        queue.push({
          id,
          operation,
          timestamp: Date.now(),
          retryCount: 0,
          data
        });
      }
      
      this.saveQueue(queue);
      logger.info(`Opération ${operation} pour la notification ${id} ajoutée à la file d'attente`);
    } catch (error) {
      logger.error(`Erreur lors de l'ajout de l'opération ${operation} pour la notification ${id} à la file d'attente:`, error);
    }
  },

  /**
   * Marque une opération comme terminée et la retire de la file d'attente
   */
  dequeue(id: string, operation: 'markAsRead' | 'delete' | 'update'): void {
    try {
      const queue = this.getQueue();
      const newQueue = queue.filter(item => !(item.id === id && item.operation === operation));
      this.saveQueue(newQueue);
      logger.info(`Opération ${operation} pour la notification ${id} retirée de la file d'attente`);
    } catch (error) {
      logger.error(`Erreur lors du retrait de l'opération ${operation} pour la notification ${id} de la file d'attente:`, error);
    }
  },

  /**
   * Incrémente le compteur de tentatives pour une opération
   */
  incrementRetryCount(id: string, operation: 'markAsRead' | 'delete' | 'update'): boolean {
    try {
      const queue = this.getQueue();
      const itemIndex = queue.findIndex(item => item.id === id && item.operation === operation);
      
      if (itemIndex >= 0) {
        queue[itemIndex].retryCount++;
        this.saveQueue(queue);
        
        // Vérifier si le nombre maximum de tentatives est atteint
        if (queue[itemIndex].retryCount >= this.MAX_RETRIES) {
          logger.warn(`Nombre maximum de tentatives atteint pour l'opération ${operation} sur la notification ${id}`);
          return false;
        }
        return true;
      }
      return false;
    } catch (error) {
      logger.error(`Erreur lors de l'incrémentation du compteur de tentatives pour l'opération ${operation} sur la notification ${id}:`, error);
      return false;
    }
  },

  /**
   * Récupère les opérations en attente
   */
  getPendingOperations(): QueueItem[] {
    return this.getQueue();
  }
};

